import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { betCodeAPI } from '../api/endpoints';
import { formatTimeAgo, getStatusColor } from '../utils/helpers';
import { 
  FaSearch, FaFilter, FaPlus, FaThumbsUp, 
  FaComment, FaEye, FaClock, FaFire, FaTicketAlt
} from 'react-icons/fa';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './BetCodes.css';

const BetCodes = () => {
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [betCodes, setBetCodes] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, pages: 0, current: 1 });
  const [filters, setFilters] = useState({
    search: '',
    platform: '',
    status: '',
    sort: 'recent',
  });
  const [showFilters, setShowFilters] = useState(false);

  const fetchBetCodes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        per_page: 20,
        page: pagination.current,
        search: filters.search || undefined,
        platform: filters.platform || undefined,
        status: filters.status || undefined,
      };
      const response = await betCodeAPI.getList(params);
      setBetCodes(response.data.data || []);
      setPagination({
        total: response.data.total || 0,
        pages: response.data.pages || 0,
        current: pagination.current,
      });
    } catch (error) {
      console.error('Failed to fetch bet codes:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.current, filters]);

  useEffect(() => {
    fetchBetCodes();
  }, [fetchBetCodes]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchBetCodes();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const clearFilters = () => {
    setFilters({ search: '', platform: '', status: '', sort: 'recent' });
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  if (loading) {
    return (
      <div className="page-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="bet-codes-page">
      <div className="page-header">
        <div>
          <h1>Bet Codes</h1>
          <p className="page-subtitle">
            Discover and share winning bet codes with the community
          </p>
        </div>
        {isAuthenticated && (
          <Link to="/bet-codes/create" className="btn btn-primary">
            <FaPlus /> Create Bet Code
          </Link>
        )}
      </div>

      {/* Search and Filters */}
      <div className="search-section">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search bet codes..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
          <button 
            type="button" 
            className="filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> Filters
          </button>
          <button type="submit" className="btn btn-primary">Search</button>
        </form>

        {showFilters && (
          <div className="filters-panel">
            <div className="filter-group">
              <label>Platform</label>
              <select 
                value={filters.platform}
                onChange={(e) => handleFilterChange('platform', e.target.value)}
              >
                <option value="">All Platforms</option>
                <option value="SportyBet">SportyBet</option>
                <option value="Bet9ja">Bet9ja</option>
                <option value="1xBet">1xBet</option>
                <option value="Betway">Betway</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select 
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="running">Running</option>
                <option value="won">Won</option>
                <option value="loose">Lost</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Sort By</label>
              <select 
                value={filters.sort}
                onChange={(e) => handleFilterChange('sort', e.target.value)}
              >
                <option value="recent">Most Recent</option>
                <option value="popular">Most Popular</option>
                <option value="winning">Highest Win Rate</option>
                <option value="odds">Highest Odds</option>
              </select>
            </div>
            <button onClick={clearFilters} className="btn btn-secondary btn-sm">
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="results-info">
        <span>{pagination.total} bet codes found</span>
      </div>

      {/* Bet Codes Grid */}
      {betCodes.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>No bet codes found</h3>
          <p>Try adjusting your search or filters</p>
        </div>
      ) : (
        <div className="bet-codes-grid">
          {betCodes.map((bet) => (
            <Link to={`/bet-codes/${bet.id}`} key={bet.id} className="bet-code-card">
              {/* Image */}
              {bet.image_url && (
                <div className="bet-code-image">
                  <img src={bet.image_url} alt={bet.title} />
                  {bet.image_type === 'gif' && (
                    <span className="image-badge">GIF</span>
                  )}
                </div>
              )}
              
              <div className="bet-code-body">
                <div className="bet-code-header">
                  <div className="bet-code-meta">
                    <span className="bet-platform">{bet.platform || 'General'}</span>
                    <span 
                      className="bet-status"
                      style={{ color: getStatusColor(bet.status) }}
                    >
                      ● {bet.status}
                    </span>
                  </div>
                  {bet.is_private && (
                    <span className="private-badge">🔒 Private</span>
                  )}
                  {bet.is_rollover && (
                    <span className="rollover-badge">🔄 Rollover</span>
                  )}
                </div>

                <h3 className="bet-code-title">{bet.title}</h3>
                
                <div className="bet-code-details">
                  <span className="bet-code-number">
                    {bet.number_of_games} Games
                  </span>
                  <span className="bet-code-odds">Odds: {bet.odds}</span>
                  {bet.win_rate > 0 && (
                    <span className="bet-code-winrate">
                      {bet.win_rate}% Win Rate
                    </span>
                  )}
                </div>

                <div className="bet-code-code">{bet.code}</div>

                <div className="bet-code-footer">
                  <div className="bet-code-author">
                    <img 
                      src={bet.author_avatar || '/default-avatar.png'} 
                      alt={bet.author}
                      className="avatar-small"
                    />
                    <span>{bet.author}</span>
                    <span className="bet-time">{formatTimeAgo(bet.created_at)}</span>
                  </div>
                  <div className="bet-code-stats">
                    <span><FaThumbsUp /> {bet.likes_count || 0}</span>
                    <span><FaComment /> {bet.comments_count || 0}</span>
                    <span><FaEye /> {bet.marks_won || 0}W/{bet.marks_loss || 0}L</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(pagination.current - 1)}
            disabled={pagination.current === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <div className="pagination-pages">
            {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
              let pageNum;
              if (pagination.pages <= 5) {
                pageNum = i + 1;
              } else if (pagination.current <= 3) {
                pageNum = i + 1;
              } else if (pagination.current >= pagination.pages - 2) {
                pageNum = pagination.pages - 4 + i;
              } else {
                pageNum = pagination.current - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`pagination-page ${pagination.current === pageNum ? 'active' : ''}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <button 
            onClick={() => handlePageChange(pagination.current + 1)}
            disabled={pagination.current === pagination.pages}
            className="pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default BetCodes;